/* @flow */

import type { Undefined } from '../jsHelpers/types';

import { ItemLocationKind, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_RECORDINGS_MAP, type NETGEM_SCHEDULED_RECORDINGS_MAP, RecordingOutcome } from '../../libs/netgemLibrary/v8/types/Npvr';
import { hasRunningSeriesScheduledRecording } from './scheduledRecording';
import { type ITEM_RECORD_STATUS } from './Types';

const isRecordingMatching: (item: NETGEM_API_V8_FEED_ITEM, recording: NETGEM_API_V8_ITEM_LOCATION) => boolean = (item, recording) => {
  const {
    selectedLocation: { duration, scheduledEventDuration, scheduledEventStartDate, startDate },
  } = item;
  const localStart = scheduledEventStartDate || startDate;
  const localDuration = scheduledEventDuration || duration;

  const { scheduledEventDuration: recordingDuration, scheduledEventStartDate: recordingStart } = recording;

  return Boolean(localStart && localDuration) && recordingStart === localStart && recordingDuration === localDuration;
};

const searchProgramInRecordings: (
  programId: string,
  item: NETGEM_API_V8_FEED_ITEM,
  recordingList: NETGEM_RECORDINGS_MAP,
) => {
  failedRecordingId?: string,
  hasRecording: boolean,
  recordOutcome?: RecordingOutcome,
  warningScheduledEventId?: string,
  warningScheduledRecordingId?: string,
} = (programId, item, recordingList) => {
  const recordings = recordingList[programId];

  let failedRecordingId: Undefined<string> = undefined;
  let hasRecording = false;
  let recordOutcome: Undefined<RecordingOutcome> = undefined;
  let warningScheduledEventId: Undefined<string> = undefined;
  let warningScheduledRecordingId: Undefined<string> = undefined;

  if (recordings) {
    recordings.forEach((r) => {
      const { recordOutcome: outcome } = r;

      if (outcome) {
        if (recordOutcome !== RecordingOutcome.Recorded) {
          // Existing recording case
          recordOutcome = outcome;
          if (outcome !== RecordingOutcome.Recorded) {
            failedRecordingId = r.id;
          } else if (isRecordingMatching(item, r)) {
            failedRecordingId = undefined;
            hasRecording = true;
          }
        }
      } else {
        // Future recording case
        const { id, matchingScheduledRecordings } = r;
        if (matchingScheduledRecordings) {
          const scheduledRecordingInDanger = matchingScheduledRecordings.find((scheduledRecording) => scheduledRecording.recordOutcome !== RecordingOutcome.WillBeRecorded);
          if (scheduledRecordingInDanger) {
            warningScheduledEventId = id;
            ({ id: warningScheduledRecordingId, recordOutcome } = scheduledRecordingInDanger);
          }
        }
      }
    });
  }

  return {
    failedRecordingId,
    hasRecording,
    recordOutcome,
    warningScheduledEventId,
    warningScheduledRecordingId,
  };
};

const getEpisodeAndSeriesRecordStatus: (
  programId: string,
  metadataSeriesId: Undefined<string>,
  item: NETGEM_API_V8_FEED_ITEM,
  npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  npvrRecordingsFuture: NETGEM_RECORDINGS_MAP,
  npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP,
  previewCatchupScheduledEventId: ?string,
) => ITEM_RECORD_STATUS = (programId, metadataSeriesId, item, npvrRecordingsList, npvrRecordingsFuture, npvrScheduledRecordingsList, previewCatchupScheduledEventId) => {
  const {
    selectedLocation: { id: scheduledEventId = '' },
    seriesId,
  } = item;

  let failedRecordingId: Undefined<string> = undefined;
  let hasRecording = false;
  let recordOutcome: Undefined<RecordingOutcome> = undefined;
  let warningScheduledEventId: Undefined<string> = undefined;
  let warningScheduledRecordingId: Undefined<string> = undefined;

  // HACK: recordings started in the user start margin does not appear in the future recording list any longer but their recordOutcome in the existing recording list is "Unknown"
  const {
    locType,
    selectedLocation: { recordOutcome: correctOutcome },
  } = item;
  const isRecordingFallback = locType === ItemLocationKind.Recording && correctOutcome === RecordingOutcome.Recorded;

  // Look into existing recordings
  ({ failedRecordingId, hasRecording, recordOutcome } = searchProgramInRecordings(programId, item, npvrRecordingsList));

  // Look into future recordings
  if (!hasRecording && !failedRecordingId && !recordOutcome) {
    ({ recordOutcome, warningScheduledEventId, warningScheduledRecordingId } = searchProgramInRecordings(programId, item, npvrRecordingsFuture));
  }

  const scheduledRecording = npvrScheduledRecordingsList[previewCatchupScheduledEventId ?? scheduledEventId];
  const hasScheduledRecording = Boolean(scheduledRecording) && ((scheduledRecording.records.length > 0 && hasRecording) || scheduledRecording.records.length === 0);
  const hasSeriesScheduledRecording = hasRunningSeriesScheduledRecording(seriesId ?? metadataSeriesId, npvrScheduledRecordingsList);

  if ((!hasRecording || recordOutcome === RecordingOutcome.Unknown) && isRecordingFallback) {
    return {
      failedRecordingId,
      hasRecording: true,
      hasScheduledRecording,
      hasSeriesScheduledRecording,
      recordOutcome: RecordingOutcome.Recorded,
      warningScheduledEventId,
      warningScheduledRecordingId,
    };
  }

  return {
    failedRecordingId,
    hasRecording,
    hasScheduledRecording,
    hasSeriesScheduledRecording,
    recordOutcome,
    warningScheduledEventId,
    warningScheduledRecordingId,
  };
};

const getScheduledRecordingIdFromRecordingId: (failedRecordingId: string, npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP) => string | null = (
  failedRecordingId,
  npvrScheduledRecordingsList,
) => {
  for (const key of Object.keys(npvrScheduledRecordingsList)) {
    const scheduledRecording = npvrScheduledRecordingsList[key];
    const { id: scheduledRecordingId, records } = scheduledRecording;
    if (records.some((r) => r.id === failedRecordingId)) {
      return scheduledRecordingId;
    }
  }

  return null;
};

const searchScheduledRecordingInFuture: (
  scheduledRecordingId: string,
  futureRecordings: NETGEM_RECORDINGS_MAP | null,
) => {
  isScheduledRecordingFuturePresent: boolean,
  scheduledRecordingOutcome: RecordingOutcome,
} = (scheduledRecordingId, futureRecordings) => {
  let isScheduledRecordingFuturePresent = false;
  let scheduledRecordingOutcome: RecordingOutcome = RecordingOutcome.WillBeRecorded;

  if (futureRecordings === null) {
    // Latest retrieved future recordings have not changed (HTTP 304)
    return { isScheduledRecordingFuturePresent, scheduledRecordingOutcome };
  }

  const futureKeys = Object.keys(futureRecordings);

  for (let i = 0; i < futureKeys.length; ++i) {
    const locations = futureRecordings[futureKeys[i]];

    for (let j = 0; j < locations.length; ++j) {
      const {
        [j]: { matchingScheduledRecordings },
      } = locations;
      if (matchingScheduledRecordings) {
        for (let k = 0; k < matchingScheduledRecordings.length; ++k) {
          const {
            [k]: { id, recordOutcome },
          } = matchingScheduledRecordings;

          if (id === scheduledRecordingId) {
            isScheduledRecordingFuturePresent = true;
            if (recordOutcome !== RecordingOutcome.WillBeRecorded && recordOutcome !== RecordingOutcome.Rebroadcast && recordOutcome !== RecordingOutcome.OutdatedEpisode) {
              scheduledRecordingOutcome = recordOutcome;
              return { isScheduledRecordingFuturePresent, scheduledRecordingOutcome };
            }
          }
        }
      }
    }
  }

  return { isScheduledRecordingFuturePresent, scheduledRecordingOutcome };
};

const searchRecordingInExisting: (
  recordId: string,
  existingRecordings: NETGEM_RECORDINGS_MAP | null,
) => {
  isRecordingPresent: boolean,
  recordingOutcome: RecordingOutcome,
} = (recordId, existingRecordings) => {
  let isRecordingPresent = false;

  if (existingRecordings === null) {
    // Latest retrieved future recordings have not changed (HTTP 304)
    return { isRecordingPresent, recordingOutcome: RecordingOutcome.Unknown };
  }

  const existingKeys = Object.keys(existingRecordings);

  for (let i = 0; i < existingKeys.length; ++i) {
    const locations = existingRecordings[existingKeys[i]];

    for (let j = 0; j < locations.length; ++j) {
      const {
        [j]: { id, recordOutcome },
      } = locations;

      if (id === recordId) {
        isRecordingPresent = true;
        if (recordOutcome === RecordingOutcome.Recorded || recordOutcome === RecordingOutcome.Rebroadcast) {
          return { isRecordingPresent, recordingOutcome: RecordingOutcome.Recorded };
        }
      }
    }
  }

  return { isRecordingPresent, recordingOutcome: RecordingOutcome.Unknown };
};

export { getEpisodeAndSeriesRecordStatus, getScheduledRecordingIdFromRecordingId, isRecordingMatching, searchRecordingInExisting, searchScheduledRecordingInFuture };
